import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { AnimalType, MedType, IndicationType } from '../types/dosage-types';
import {
  TreatmentDataService,
  AnimalOption,
  AilmentOption,
  MedicationOption,
  ToolOption
} from '../services/treatment-data-service';
import AppAnalytics from '../analytics/AppAnalytics';

// Use interfaces from the service

@customElement('animal-treatment-selector')
export class AnimalTreatmentSelector extends LitElement {
  @property({ type: String }) preselectedAnimal = '';
  @property({ type: String }) preselectedAilment = '';
  @property({ type: String }) preselectedMedication = '';
  @property({ type: String }) preselectedTool = '';
  @property({ type: Boolean }) lockSelections = false;

  @state() private currentStep = 1;
  @state() private selectedAnimal = '';
  @state() private selectedAilment = '';
  @state() private selectedMedication = '';
  @state() private selectedTool = '';

  // Use light DOM for Tailwind CSS
  createRenderRoot() {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    this.initializePreselections();
  }

  private initializePreselections() {
    if (this.preselectedAnimal) {
      this.selectedAnimal = this.preselectedAnimal;
      this.currentStep = this.lockSelections ? this.getCurrentStepFromPreselections() : 2;
    }
    if (this.preselectedAilment) {
      this.selectedAilment = this.preselectedAilment;
      this.currentStep = this.lockSelections ? this.getCurrentStepFromPreselections() : 3;
    }
    if (this.preselectedMedication) {
      this.selectedMedication = this.preselectedMedication;
      this.currentStep = this.lockSelections ? this.getCurrentStepFromPreselections() : 4;
    }
    if (this.preselectedTool) {
      this.selectedTool = this.preselectedTool;
      this.currentStep = 4;
    }
  }

  private getCurrentStepFromPreselections(): number {
    if (this.preselectedTool) return 4;
    if (this.preselectedMedication) return 4;
    if (this.preselectedAilment) return 3;
    if (this.preselectedAnimal) return 2;
    return 1;
  }

  private getProgressWidth(): string {
    const progress = ((this.currentStep - 1) / 3) * 100;
    return `${Math.min(progress, 100)}%`;
  }

  private openTool() {
    const selectedTool = this.getSelectedTool();
    if (selectedTool && selectedTool.url) {
      // Track tool opening
      AppAnalytics.getPlausible().trackEvent('Tool Opened', {
        props: {
          animal: this.selectedAnimal,
          condition: this.selectedAilment,
          medication: this.selectedMedication,
          tool: this.selectedTool
        }
      });

      window.location.href = selectedTool.url;
    }
  }

  private getAvailableAilments(): AilmentOption[] {
    if (!this.selectedAnimal) return [];
    return TreatmentDataService.getAilmentsForAnimal(this.selectedAnimal as AnimalType);
  }

  private getAvailableMedications(): MedicationOption[] {
    if (!this.selectedAilment) return [];
    return TreatmentDataService.getMedicationsForAilment(this.selectedAilment);
  }

  private getAvailableTools(): ToolOption[] {
    if (!this.selectedMedication) return [];
    return TreatmentDataService.getToolsForMedication(this.selectedMedication as MedType);
  }

  private selectAnimal(animalId: string) {
    if (this.lockSelections && this.preselectedAnimal) return;
    this.selectedAnimal = animalId;
    this.selectedAilment = '';
    this.selectedMedication = '';
    this.selectedTool = '';

    // Track animal selection
    AppAnalytics.getPlausible().trackEvent('Animal Selected', {
      props: {
        animal: animalId
      }
    });

    this.requestUpdate();
  }

  private selectAilment(ailmentId: string) {
    if (this.lockSelections && this.preselectedAilment) return;
    this.selectedAilment = ailmentId;
    this.selectedMedication = '';
    this.selectedTool = '';

    // Track ailment selection
    AppAnalytics.getPlausible().trackEvent('Condition Selected', {
      props: {
        animal: this.selectedAnimal,
        condition: ailmentId
      }
    });

    this.requestUpdate();
  }

  private selectMedication(medicationId: string) {
    if (this.lockSelections && this.preselectedMedication) return;
    this.selectedMedication = medicationId;
    this.selectedTool = '';

    // Track medication selection
    AppAnalytics.getPlausible().trackEvent('Treatment Selected', {
      props: {
        animal: this.selectedAnimal,
        condition: this.selectedAilment,
        medication: medicationId
      }
    });

    this.requestUpdate();
  }

  private selectTool(toolId: string) {
    if (this.lockSelections && this.preselectedTool) return;
    this.selectedTool = toolId;
      this.requestUpdate();
  }

  private nextStep() {
    if (this.currentStep < 4) {
      this.currentStep++;
      this.requestUpdate();
    }
  }

  private prevStep() {
    if (this.currentStep > 1) {
      this.currentStep--;
      this.requestUpdate();
    }
  }

  private canProceed(): boolean {
    switch (this.currentStep) {
      case 1: return !!this.selectedAnimal;
      case 2: return !!this.selectedAilment;
      case 3: return !!this.selectedMedication;
      case 4: return !!this.selectedTool;
      default: return false;
    }
  }

  private generateCalculatorUrl(): string {
    if (!this.selectedAnimal || !this.selectedMedication) {
      return '';
    }

    return TreatmentDataService.getTreatmentUrl(
      this.selectedAnimal as AnimalType,
      this.selectedMedication as MedType,
      IndicationType.TREATMENT
    );
  }

  private getSelectedTool(): ToolOption | undefined {
    const tools = this.getAvailableTools();
    const tool = tools.find(tool => tool.id === this.selectedTool);
    if (tool) {
      // Generate the URL dynamically based on current selections
      return {
        ...tool,
        url: this.generateCalculatorUrl()
      };
    }
    return undefined;
  }

  private renderProgressBar() {
    return html`
      <div class="flex justify-between mb-12 relative">
        <!-- Background line -->
        <div class="absolute top-4 left-8 right-8 h-0.5 bg-gray-200 z-10"></div>

        <!-- Progress line -->
        <div
          class="absolute top-4 left-8 h-0.5 z-20 transition-all duration-700 ease-out"
          style="width: ${this.getProgressWidth()}; background: var(--vetri-gradient-primary);"
        ></div>

        <!-- Step 1: Animal -->
        <div class="flex flex-col items-center relative z-30">
          <div class="w-8 h-8 rounded-full border-2 flex items-center justify-center font-semibold transition-all duration-300 transform
            ${this.currentStep >= 1 ? (this.currentStep === 1 ? 'border-blue-500 bg-blue-500 text-white scale-110' : 'border-green-500 bg-green-500 text-white') : 'border-gray-300 bg-white text-gray-500'}">
            ${this.currentStep > 1 ? '✓' : '1'}
          </div>
          <div class="mt-2 text-sm font-medium transition-colors duration-300 ${this.currentStep === 1 ? 'text-blue-600' : 'text-gray-500'}">Animal</div>
        </div>

        <!-- Step 2: Condition -->
        <div class="flex flex-col items-center relative z-30">
          <div class="w-8 h-8 rounded-full border-2 flex items-center justify-center font-semibold transition-all duration-300 transform
            ${this.currentStep >= 2 ? (this.currentStep === 2 ? 'border-blue-500 bg-blue-500 text-white scale-110' : 'border-green-500 bg-green-500 text-white') : 'border-gray-300 bg-white text-gray-500'}">
            ${this.currentStep > 2 ? '✓' : '2'}
          </div>
          <div class="mt-2 text-sm font-medium transition-colors duration-300 ${this.currentStep === 2 ? 'text-blue-600' : 'text-gray-500'}">Condition</div>
        </div>

        <!-- Step 3: Treatment -->
        <div class="flex flex-col items-center relative z-30">
          <div class="w-8 h-8 rounded-full border-2 flex items-center justify-center font-semibold transition-all duration-300 transform
            ${this.currentStep >= 3 ? (this.currentStep === 3 ? 'border-blue-500 bg-blue-500 text-white scale-110' : 'border-green-500 bg-green-500 text-white') : 'border-gray-300 bg-white text-gray-500'}">
            ${this.currentStep > 3 ? '✓' : '3'}
          </div>
          <div class="mt-2 text-sm font-medium transition-colors duration-300 ${this.currentStep === 3 ? 'text-blue-600' : 'text-gray-500'}">Treatment</div>
        </div>

        <!-- Step 4: Tool -->
        <div class="flex flex-col items-center relative z-30">
          <div class="w-8 h-8 rounded-full border-2 flex items-center justify-center font-semibold transition-all duration-300 transform
            ${this.currentStep >= 4 ? (this.currentStep === 4 ? 'border-blue-500 bg-blue-500 text-white scale-110' : 'border-green-500 bg-green-500 text-white') : 'border-gray-300 bg-white text-gray-500'}">
            ${this.currentStep > 4 ? '✓' : '4'}
          </div>
          <div class="mt-2 text-sm font-medium transition-colors duration-300 ${this.currentStep === 4 ? 'text-blue-600' : 'text-gray-500'}">Tool</div>
        </div>
      </div>
    `;
  }

  private renderAnimalSelection() {
    return html`
      <div class="min-h-96 flex flex-col vetri-animate-slide-in">
        <h2 class="text-3xl font-bold text-gray-900 mb-2 text-center">Select Your Animal</h2>
        <p class="text-gray-600 text-center mb-8">Choose the type of animal you need treatment information for</p>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8 flex-grow">
          ${TreatmentDataService.getAnimals().map(animal => html`
            <div
              class="p-6 border-2 rounded-xl cursor-pointer transition-all duration-300 text-center transform hover:-translate-y-1
                ${this.selectedAnimal === animal.id ? 'border-blue-500 bg-blue-50 shadow-lg shadow-blue-200/50' : 'border-gray-200 bg-white hover:border-blue-400 hover:shadow-lg'}
                ${this.lockSelections && this.preselectedAnimal && this.preselectedAnimal !== animal.id ? 'opacity-50 cursor-not-allowed' : ''}"
              @click=${() => this.selectAnimal(animal.id)}
            >
              <span class="text-4xl mb-3 block vetri-animate-gentle-bounce">${animal.icon}</span>
              <div class="font-semibold text-gray-900 mb-2">${animal.name}</div>
              <div class="text-sm text-gray-600">${animal.description}</div>
            </div>
          `)}
        </div>

        <div class="flex justify-between items-center pt-6 border-t border-gray-100">
          <div></div>
          <button
            class="px-8 py-3 rounded-lg font-semibold text-white transition-all duration-300 transform hover:-translate-y-0.5 focus:outline-none focus:ring-4 focus:ring-blue-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            style="background: var(--vetri-gradient-primary); box-shadow: var(--vetri-shadow-md);"
            ?disabled=${!this.canProceed()}
            @click=${this.nextStep}
          >
            Next Step →
          </button>
        </div>
      </div>
    `;
  }

  private renderAilmentSelection() {
    const availableAilments = this.getAvailableAilments();

    return html`
      <div class="min-h-96 flex flex-col vetri-animate-slide-in">
        <h2 class="text-3xl font-bold text-gray-900 mb-2 text-center">Select the Condition</h2>
        <p class="text-gray-600 text-center mb-8">What health condition are you treating?</p>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8 flex-grow">
          ${availableAilments.map(ailment => html`
            <div
              class="p-6 border-2 rounded-xl cursor-pointer transition-all duration-300 text-center transform hover:-translate-y-1
                ${this.selectedAilment === ailment.id ? 'border-blue-500 bg-blue-50 shadow-lg shadow-blue-200/50' : 'border-gray-200 bg-white hover:border-blue-400 hover:shadow-lg'}
                ${this.lockSelections && this.preselectedAilment && this.preselectedAilment !== ailment.id ? 'opacity-50 cursor-not-allowed' : ''}"
              @click=${() => this.selectAilment(ailment.id)}
            >
              <div class="font-semibold text-gray-900 mb-2">${ailment.name}</div>
              <div class="text-sm text-gray-600 leading-relaxed">${ailment.description}</div>
            </div>
          `)}
        </div>

        <div class="flex justify-between items-center pt-6 border-t border-gray-100">
          <button
            class="px-6 py-3 rounded-lg font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors duration-300 focus:outline-none focus:ring-4 focus:ring-gray-200"
            @click=${this.prevStep}
          >
            ← Back
          </button>
          <button
            class="px-8 py-3 rounded-lg font-semibold text-white transition-all duration-300 transform hover:-translate-y-0.5 focus:outline-none focus:ring-4 focus:ring-blue-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            style="background: var(--vetri-gradient-primary); box-shadow: var(--vetri-shadow-md);"
            ?disabled=${!this.canProceed()}
            @click=${this.nextStep}
          >
            Next Step →
          </button>
        </div>
      </div>
    `;
  }

  private renderMedicationSelection() {
    const availableMedications = this.getAvailableMedications();

    return html`
      <div class="min-h-96 flex flex-col vetri-animate-slide-in">
        <h2 class="text-3xl font-bold text-gray-900 mb-2 text-center">Choose Treatment</h2>
        <p class="text-gray-600 text-center mb-8">Select the medication for treatment</p>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8 flex-grow">
          ${availableMedications.map(medication => html`
            <div
              class="p-6 border-2 rounded-xl cursor-pointer transition-all duration-300 text-center transform hover:-translate-y-1
                ${this.selectedMedication === medication.id ? 'border-blue-500 bg-blue-50 shadow-lg shadow-blue-200/50' : 'border-gray-200 bg-white hover:border-blue-400 hover:shadow-lg'}
                ${this.lockSelections && this.preselectedMedication && this.preselectedMedication !== medication.id ? 'opacity-50 cursor-not-allowed' : ''}"
              @click=${() => this.selectMedication(medication.id)}
            >
              <div class="font-semibold text-gray-900 mb-2">${medication.name}</div>
              <div class="text-sm text-gray-600 leading-relaxed">${medication.description}</div>
            </div>
          `)}
        </div>

        <div class="flex justify-between items-center pt-6 border-t border-gray-100">
          <button
            class="px-6 py-3 rounded-lg font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors duration-300 focus:outline-none focus:ring-4 focus:ring-gray-200"
            @click=${this.prevStep}
          >
            ← Back
          </button>
          <button
            class="px-8 py-3 rounded-lg font-semibold text-white transition-all duration-300 transform hover:-translate-y-0.5 focus:outline-none focus:ring-4 focus:ring-blue-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            style="background: var(--vetri-gradient-primary); box-shadow: var(--vetri-shadow-md);"
            ?disabled=${!this.canProceed()}
            @click=${this.nextStep}
          >
            Next Step →
          </button>
        </div>
      </div>
    `;
  }

  private renderToolSelection() {
    const availableTools = this.getAvailableTools();

    return html`
      <div class="min-h-96 flex flex-col vetri-animate-slide-in">
        <h2 class="text-3xl font-bold text-gray-900 mb-2 text-center">Select Tool</h2>
        <p class="text-gray-600 text-center mb-8">Choose the type of tool you need</p>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8 flex-grow">
          ${availableTools.map(tool => html`
            <div
              class="p-6 border-2 rounded-xl cursor-pointer transition-all duration-300 text-center transform hover:-translate-y-1
                ${this.selectedTool === tool.id ? 'border-blue-500 bg-blue-50 shadow-lg shadow-blue-200/50' : 'border-gray-200 bg-white hover:border-blue-400 hover:shadow-lg'}
                ${this.lockSelections && this.preselectedTool && this.preselectedTool !== tool.id ? 'opacity-50 cursor-not-allowed' : ''}"
              @click=${() => this.selectTool(tool.id)}
            >
              <div class="font-semibold text-gray-900 mb-2">${tool.name}</div>
              <div class="text-sm text-gray-600 leading-relaxed">${tool.description}</div>
              <div class="mt-3 text-xs text-blue-600 font-medium uppercase tracking-wider">${tool.type}</div>
            </div>
          `)}
        </div>

        <div class="flex justify-between items-center pt-6 border-t border-gray-100">
          <button
            class="px-6 py-3 rounded-lg font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors duration-300 focus:outline-none focus:ring-4 focus:ring-gray-200"
            @click=${this.prevStep}
          >
            ← Back
          </button>
          <button
            class="px-8 py-3 rounded-lg font-semibold text-white transition-all duration-300 transform hover:-translate-y-0.5 focus:outline-none focus:ring-4 focus:ring-blue-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            style="background: var(--vetri-gradient-primary); box-shadow: var(--vetri-shadow-md);"
            ?disabled=${!this.canProceed()}
            @click=${this.openTool}
          >
            Open Tool
          </button>
        </div>
      </div>
    `;
  }


  private renderCurrentStep() {
    switch (this.currentStep) {
      case 1: return this.renderAnimalSelection();
      case 2: return this.renderAilmentSelection();
      case 3: return this.renderMedicationSelection();
      case 4: return this.renderToolSelection();
      default: return this.renderAnimalSelection();
    }
  }

  render() {
    return html`
      <div class="max-w-4xl mx-auto p-8 bg-white rounded-2xl shadow-2xl">
        ${this.renderProgressBar()}
        ${this.renderCurrentStep()}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'animal-treatment-selector': AnimalTreatmentSelector;
  }
}
